import React, { useMemo, useState } from 'react';
import { Input } from 'antd';
import { ArrowLeftOutlined, CheckOutlined, SearchOutlined } from '@ant-design/icons';
import 'components/entity-picker/entity-picker.scss';

export interface PickerItem {
    key: string; // identidad única de la fila (para React y para el callback)
    id: string; // lo que se compara contra selectedId
    groupName: string;
    title: string;
    subtitle: string;
    icon?: React.ReactNode;
}

export interface ItemGroup {
    groupName: string;
    items: PickerItem[];
}

export interface EntityPickerProps {
    items: PickerItem[];
    selectedId?: string;
    title: string;
    searchPlaceholder: string;
    backLabel: string;
    emptyLabel: string;
    onSelect: (item: PickerItem) => void;
    onClose: () => void;
}

// Sin acentos y en minúscula: "porton" tiene que encontrar "Portón principal".
// El rango \u0300-\u036f son las marcas diacríticas que NFD deja sueltas; va
// escapado a propósito, no como caracteres combinantes literales (invisibles en
// el editor y fáciles de romper en un copy/paste).
const norm = (s: string): string =>
    s
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');

// Conserva el orden de entrada en vez de reordenar: quien arma la lista decide
// el criterio, y así cambia en un solo lado.
export function groupItems(items: PickerItem[], query: string): ItemGroup[] {
    const q = norm(query.trim());
    const match = (i: PickerItem) => !q || norm(i.title).includes(q) || norm(i.groupName).includes(q);

    const groups: ItemGroup[] = [];
    for (const item of items) {
        if (!match(item)) {
            continue;
        }
        const found = groups.find((g) => g.groupName === item.groupName);
        if (found) {
            found.items.push(item);
        } else {
            groups.push({ groupName: item.groupName, items: [item] });
        }
    }
    return groups;
}

export const EntityPicker: React.FC<EntityPickerProps> = ({
    items,
    selectedId,
    title,
    searchPlaceholder,
    backLabel,
    emptyLabel,
    onSelect,
    onClose,
}) => {
    const [query, setQuery] = useState('');
    const groups = useMemo(() => groupItems(items, query), [items, query]);

    return (
        <div className={'va-picker'}>
            <div className={'va-picker-nav'}>
                <button className={'va-picker-back'} aria-label={backLabel} onClick={onClose}>
                    <ArrowLeftOutlined aria-hidden />
                </button>
                <span className={'va-picker-title'}>{title}</span>
            </div>

            <div className={'va-picker-search'}>
                <Input
                    size={'large'}
                    prefix={<SearchOutlined aria-hidden />}
                    style={{ borderRadius: 8, height: 48 }}
                    placeholder={searchPlaceholder}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                />
            </div>

            {groups.length === 0 ? (
                <div className={'va-picker-empty'}>{emptyLabel}</div>
            ) : (
                <div role={'listbox'} aria-label={title}>
                    {groups.map((group) => (
                        <div key={group.groupName}>
                            <div className={'va-picker-site'}>{group.groupName}</div>
                            {group.items.map((item) => {
                                const selected = item.id === selectedId;
                                return (
                                    <button
                                        key={item.key}
                                        role={'option'}
                                        aria-selected={selected}
                                        className={`va-picker-row${selected ? ' va-picker-row--selected' : ''}`}
                                        onClick={() => onSelect(item)}
                                    >
                                        {item.icon}
                                        <span className={'va-picker-row-text'}>
                                            <span className={'va-picker-row-name'}>{item.title}</span>
                                            <span className={'va-picker-row-sub'}>{item.subtitle}</span>
                                        </span>
                                        {selected && <CheckOutlined aria-hidden className={'va-picker-check'} />}
                                    </button>
                                );
                            })}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};
